import { useEffect, useState } from 'react'

interface UseTypingPlaceholderOptions {
    placeholders: string[]
    typingSpeed?: number // milliseconds per character
    deletingSpeed?: number // milliseconds per character when deleting
    pauseDuration?: number // milliseconds to pause after typing complete
}

export const useTypingPlaceholder = ({
    placeholders,
    typingSpeed = 100,
    deletingSpeed = 50,
    pauseDuration = 2000,
}: UseTypingPlaceholderOptions) => {
    const [currentPlaceholder, setCurrentPlaceholder] = useState('')
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => {
        if (placeholders.length === 0) return

        const targetText = placeholders[currentIndex]

        const timeout = setTimeout(
            () => {
                if (!isDeleting) {
                    // Typing mode
                    if (currentPlaceholder.length < targetText.length) {
                        setCurrentPlaceholder(
                            targetText.slice(0, currentPlaceholder.length + 1)
                        )
                    } else {
                        // Finished typing, pause then start deleting
                        setTimeout(() => {
                            setIsDeleting(true)
                        }, pauseDuration)
                    }
                } else {
                    // Deleting mode
                    if (currentPlaceholder.length > 0) {
                        setCurrentPlaceholder(currentPlaceholder.slice(0, -1))
                    } else {
                        // Finished deleting, move to next placeholder
                        setIsDeleting(false)
                        setCurrentIndex(
                            (prev) => (prev + 1) % placeholders.length
                        )
                    }
                }
            },
            isDeleting ? deletingSpeed : typingSpeed
        )

        return () => clearTimeout(timeout)
    }, [
        currentPlaceholder,
        currentIndex,
        isDeleting,
        placeholders,
        typingSpeed,
        deletingSpeed,
        pauseDuration,
    ])

    return currentPlaceholder
}
